import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Switch } from '@/shared/components/ui/switch';
import { Label } from '@/shared/components/ui/label';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { useTemplate } from '../hooks/useTemplate';
import { useCreateTemplate, useUpdateTemplate } from '../hooks/useTemplateMutations';
import { FormFieldsEditor } from '../components/FormBuilder/FormFieldsEditor';
import { FormPreviewPane } from '../components/FormBuilder/FormPreviewPane';
import { toFieldInputPayload, fromFormDetailField } from '../adapters/field-type.adapter';
import type { BuilderField } from '../components/FormBuilder/FormFieldEditorRow';
import type { CreateTemplatePayload, TemplateDetail } from '../types';

export default function TemplateEditorPage() {
  const { t } = useTranslation('forms');
  const navigate = useNavigate();
  const { orgId, templateId } = useParams<{ orgId: string; templateId: string }>();
  const isNew = templateId === 'new';

  const { template, isLoading } = useTemplate(isNew ? '' : (templateId ?? ''));
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate(isNew ? '' : (templateId ?? ''));

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [builderFields, setBuilderFields] = useState<BuilderField[]>([]);
  const [emailRequired, setEmailRequired] = useState(false);

  // Seeded to `null` (never a valid template), not `template` — a warm query cache can
  // already hold `template` synchronously on the very first render of a fresh mount, which
  // would make an initializer of `template` equal itself immediately and silently skip this
  // sync forever.
  const [syncedTemplate, setSyncedTemplate] = useState<TemplateDetail | null>(null);
  if (template && template !== syncedTemplate) {
    setSyncedTemplate(template);
    setTitle(template.title);
    setDescription(template.description ?? '');
    setEmailRequired(template.emailRequired);
    setBuilderFields(
      template.fields.map((f) => ({
        ...fromFormDetailField(f),
        localId: f.id ?? crypto.randomUUID(),
      })),
    );
  }

  if (!isNew && (isLoading || !template)) {
    return (
      <div className="p-4 md:p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  function buildPayload(): CreateTemplatePayload {
    return {
      title,
      description: description || undefined,
      emailRequired,
      fields: builderFields
        .slice()
        .sort((a, b) => a.order - b.order)
        .map(toFieldInputPayload),
    };
  }

  async function handleSave() {
    try {
      if (isNew) {
        const { data: created } = await createTemplate.mutateAsync(buildPayload());
        navigate(`/${orgId}/forms/templates/${created.id}`, { replace: true });
        return;
      }
      await updateTemplate.mutateAsync(buildPayload());
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  const isSaving = createTemplate.isPending || updateTemplate.isPending;

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <div className="p-4 md:p-6 space-y-4" data-testid="forms-template-editor-page">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/${orgId}/forms`)}
              aria-label={t('list.title')}
              data-testid="forms-template-editor-back-button"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1
              className="text-lg font-semibold truncate max-w-[240px] md:max-w-sm"
              data-testid="forms-template-editor-title-label"
            >
              {title || t('templates.editor.newTitle')}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                id="forms-template-editor-email-required-toggle"
                checked={emailRequired}
                onCheckedChange={setEmailRequired}
                data-testid="forms-template-editor-email-required-toggle"
              />
              <Label
                htmlFor="forms-template-editor-email-required-toggle"
                className="text-sm font-normal"
              >
                {t('templates.editor.emailRequiredLabel')}
              </Label>
            </div>
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={isSaving || !title.trim()}
              data-testid="forms-template-editor-save-button"
            >
              {t('templates.editor.saveButton')}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-[380px_1fr]">
          <FormFieldsEditor fields={builderFields} onChange={setBuilderFields} />
          <FormPreviewPane
            title={title}
            description={description}
            fields={builderFields}
            emailRequired={emailRequired}
            onTitleChange={setTitle}
            onDescriptionChange={setDescription}
          />
        </div>
      </div>
    </FeatureErrorBoundary>
  );
}
